// TopBar - collapsible app chrome hosting the replay toggle/banner/scrub bar
// and the config gear button (TD043 Phase 1 follow-up).
//
// Collapsed to a thin pull-tab by default. It expands when the handle receives
// pointer entry or keyboard focus, or on a handle tap. Touch has no hover, so
// the tap is the only way to reach the gear on a phone or tablet. Without it,
// users cannot open the config modal. Replay also forces the bar open. It pushes
// the grid down rather than overlaying it (an overlay would sit on top of
// actionable widget tabs while transparent/collapsed, which reads as
// broken). Pointer leave clears tap-expanded state so hover CSS and JS
// state cannot disagree. Keyboard Tab focus holds the bar open; programmatic
// focus does not. Closing the config modal and exiting replay both release
// focus rather than restoring it into the bar, so neither leaves the bar
// pinned. Touch dismisses with a tap outside the bar.

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import usePlaybackStore from '../../Store/playbackStore';
import ReplayControls from './ReplayControls';
import '../../Styles/topBar.css';

export default function TopBar({
  onOpenConfig, onRecoverReplayHistory = null, replayDates = [], replayDateDiscoveryReady = true,
}) {
  const replaying = usePlaybackStore((s) => s.mode === 'playing');
  const [expanded, setExpanded] = useState(false);
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  const [replayHold, setReplayHold] = useState(false);
  const barRef = useRef(null);
  const handleRef = useRef(null);
  const contentRef = useRef(null);
  const tabIntentRef = useRef(false);
  const ignoreMouseLeaveUntilRef = useRef(0);
  const forcedOpen = expanded || replaying || keyboardOpen || replayHold;

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Tab') tabIntentRef.current = true;
    };
    const onPointerDown = () => {
      tabIntentRef.current = false;
    };
    const consumeTabIntent = () => {
      tabIntentRef.current = false;
    };
    window.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('pointerdown', onPointerDown, true);
    // Bubble phase so onFocusCapture can read Tab intent first. Any following
    // focus (in or out of the bar) consumes it, so a later focus() cannot pin.
    window.addEventListener('focusin', consumeTabIntent);
    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('pointerdown', onPointerDown, true);
      window.removeEventListener('focusin', consumeTabIntent);
    };
  }, []);

  useEffect(() => {
    if (replaying) setReplayHold(false);
  }, [replaying]);

  useEffect(() => {
    const collapseForInactivePage = () => {
      if (replaying || replayHold) return;
      const root = barRef.current;
      const handle = handleRef.current;
      if (root?.contains(document.activeElement) && document.activeElement !== handle) {
        handle?.focus();
      }
      setExpanded(false);
      setKeyboardOpen(false);
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') collapseForInactivePage();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('blur', collapseForInactivePage);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('blur', collapseForInactivePage);
    };
  }, [replaying, replayHold]);

  useLayoutEffect(() => {
    if (forcedOpen) return;
    const root = barRef.current;
    if (root) root.scrollTop = 0;
  }, [forcedOpen]);

  useEffect(() => {
    if (!expanded || replaying || replayHold) return undefined;
    const onPointerDown = (e) => {
      const root = barRef.current;
      if (!root || root.contains(e.target)) return;
      setExpanded(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [expanded, replaying, replayHold]);

  const collapseFromPointerLeave = () => {
    if (replaying || replayHold) return;
    if (Date.now() < ignoreMouseLeaveUntilRef.current) return;
    setExpanded(false);
  };

  const onHandlePointerUp = (e) => {
    if (e.pointerType === 'touch' || e.pointerType === 'pen') {
      ignoreMouseLeaveUntilRef.current = Date.now() + 1000;
    }
  };

  const onFocusCapture = () => {
    if (tabIntentRef.current) {
      setKeyboardOpen(true);
      return;
    }
    const root = barRef.current;
    if (root) root.scrollTop = 0;
  };

  const onBlurCapture = (e) => {
    const next = e.relatedTarget;
    if (next && e.currentTarget.contains(next)) return;
    setKeyboardOpen(false);
  };

  return (
    <div
      ref={barRef}
      className={`top-bar${forcedOpen ? ' top-bar--expanded' : ''}${replaying ? ' top-bar--replaying' : ''}`}
      onMouseLeave={collapseFromPointerLeave}
      onFocusCapture={onFocusCapture}
      onBlurCapture={onBlurCapture}
    >
      <button
        ref={handleRef}
        type="button"
        className="top-bar__handle"
        aria-expanded={forcedOpen}
        aria-label={
          replaying
            ? 'Dashboard controls shown during replay'
            : forcedOpen ? 'Hide dashboard controls' : 'Show dashboard controls'
        }
        disabled={replaying}
        onMouseEnter={() => { setExpanded(true); }}
        onKeyDown={(e) => {
          if (e.key !== 'Tab' || e.shiftKey || forcedOpen) return;
          e.preventDefault();
          flushSync(() => { setKeyboardOpen(true); });
          contentRef.current?.querySelector('button:not(:disabled), input:not(:disabled)')?.focus();
        }}
        onPointerUp={onHandlePointerUp}
        onClick={() => {
          // Track the visible state, not `expanded` alone: the bar can be open
          // on a keyboard or replay hold, and toggling `expanded` against that
          // would leave it open and make the "Hide" handle a dead click.
          setKeyboardOpen(false);
          setReplayHold(false);
          setExpanded(!forcedOpen);
        }}
      />
      <div className="top-bar__content" ref={contentRef}>
        <ReplayControls
          controlTabIndex={forcedOpen ? undefined : -1}
          onRecoverReplayHistory={onRecoverReplayHistory}
          replayDates={replayDates}
          replayDateDiscoveryReady={replayDateDiscoveryReady}
          onReplayRequest={() => {
            setExpanded(true);
            setReplayHold(true);
          }}
          onReplayRequestSettled={() => {
            setReplayHold(false);
            const playback = usePlaybackStore.getState();
            if (
              playback.mode !== 'playing'
              && playback.bufferFrames.length === 0
              && !playback.historyError
            ) {
              setExpanded(false);
            }
          }}
          onReplayExit={() => {
            setExpanded(false);
            setReplayHold(false);
          }}
        />
        <button
          type="button"
          className="dashboard-config-btn"
          tabIndex={forcedOpen ? undefined : -1}
          onClick={onOpenConfig}
          title="Configure layout"
          aria-label="Configure dashboard layout"
        >
          {'\u2699'}
        </button>
      </div>
    </div>
  );
}
